import React, { useEffect, useMemo, useState } from 'react';
import { FiSend, FiSearch, FiCheckSquare, FiSquare } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import useEntity from '../hooks/useEntity';
import { useAuth } from '../contexts/AuthContext';
import { puede } from '../utils/permisos';

const ESTADOS_CRM = ['prospecto', 'contactado', 'presupuesto_enviado', 'negociando', 'ganado', 'perdido'];
const ETIQUETA_CRM = {
  prospecto: 'Prospecto',
  contactado: 'Contactado',
  presupuesto_enviado: 'Presupuesto enviado',
  negociando: 'Negociando',
  ganado: 'Ganado',
  perdido: 'Perdido',
};

export default function Comunicados() {
  const { usuario, empresa } = useAuth();
  const navigate = useNavigate();
  const { items, cargando, error } = useEntity('clientes');
  const [busqueda, setBusqueda] = useState('');
  const [filtroCrm, setFiltroCrm] = useState('');
  const [seleccionados, setSeleccionados] = useState([]);
  const [asunto, setAsunto] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [fallidos, setFallidos] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [reintentando, setReintentando] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroDesde, setFiltroDesde] = useState('');
  const [filtroHasta, setFiltroHasta] = useState('');
  const [filtroCampanya, setFiltroCampanya] = useState('');
  const [filtroScope, setFiltroScope] = useState('');
  const [vista, setVista] = useState('editor');
  const [campaignTag, setCampaignTag] = useState('');
  const [scope, setScope] = useState('seleccionado');

  const smtpConfigurado = Boolean(empresa?.smtpConfigurado);
  const puedeEnviar = puede(usuario, 'clientes', 'crear') && smtpConfigurado;

  useEffect(() => {
    if (!smtpConfigurado) {
      console.warn('Comunicados: falta configurar SMTP de la empresa');
      return;
    }

    api.get('/comunicados/fallidos')
      .then(({ data }) => setFallidos(Array.isArray(data) ? data : []))
      .catch(() => setFallidos([]));

    const params = {};
    if (filtroEstado) params.estado = filtroEstado;
    if (filtroCliente) params.buscar = filtroCliente;
    if (filtroDesde) params.desde = filtroDesde;
    if (filtroHasta) params.hasta = filtroHasta;
    if (filtroCampanya) params.campaignTag = filtroCampanya;
    if (filtroScope) params.scope = filtroScope;

    api.get('/email-logs', { params })
      .then(({ data }) => setHistorial(Array.isArray(data) ? data : []))
      .catch(() => setHistorial([]));
  }, [smtpConfigurado, filtroEstado, filtroCliente, filtroDesde, filtroHasta, filtroCampanya, filtroScope]);

  const filtrados = useMemo(() => {
    return (items || []).filter((cliente) => {
      const textoOk = [cliente.nombre, cliente.email, cliente.telefono, cliente.nif]
        .join(' ')
        .toLowerCase()
        .includes(busqueda.toLowerCase());
      const crmOk = filtroCrm === '' || cliente.estadoCrm === filtroCrm;
      const tieneEmail = Boolean(cliente.email && cliente.email.trim());
      return textoOk && crmOk && tieneEmail;
    });
  }, [items, busqueda, filtroCrm]);

  const visiblesSeleccionadas = filtrados.map((c) => c._id);
  const todasVisiblesSeleccionadas = filtrados.length > 0 && visiblesSeleccionadas.every((id) => seleccionados.includes(id));

  const toggleCliente = (id) => {
    setSeleccionados((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleTodosVisibles = () => {
    if (todasVisiblesSeleccionadas) {
      setSeleccionados((prev) => prev.filter((id) => !visiblesSeleccionadas.includes(id)));
      return;
    }

    setSeleccionados((prev) => {
      const union = new Set(prev);
      visiblesSeleccionadas.forEach((id) => union.add(id));
      return [...union];
    });
  };

  const onEnviar = async (e) => {
    e.preventDefault();
    if (!smtpConfigurado) {
      alert('No puedes enviar comunicados porque no hay SMTP configurado ni en empresa ni en entorno global.');
      return;
    }
    if (!puedeEnviar) return;
    if (!asunto.trim()) return alert('Escribe un asunto para el comunicado');
    if (scope === 'seleccionado' && seleccionados.length === 0) return alert('Selecciona al menos un cliente');
    if (!mensaje.trim()) return alert('Escribe el contenido del comunicado');

    setEnviando(true);
    setResultado(null);

    try {
      const { data } = await api.post('/comunicados/enviar', {
        clienteIds: scope === 'seleccionado' ? seleccionados : [],
        asunto,
        cuerpoHtml: `<p>${mensaje.replace(/\n/g, '<br />')}</p>`,
        cuerpoTexto: mensaje,
        campaignTag: campaignTag.trim(),
        scope,
        todos: scope === 'todos',
      });
      setResultado(data);
      setSeleccionados([]);
      setAsunto('');
      setMensaje('');
      setCampaignTag('');
      setScope('seleccionado');

      const { data: fallidosData } = await api.get('/comunicados/fallidos');
      setFallidos(Array.isArray(fallidosData) ? fallidosData : []);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo enviar el comunicado');
    } finally {
      setEnviando(false);
    }
  };

  const onReintentarFallidos = async () => {
    if (!smtpConfigurado) {
      alert('No puedes reintentar comunicados fallidos porque no hay SMTP configurado ni en empresa ni en entorno global.');
      return;
    }

    setReintentando(true);
    try {
      const { data } = await api.post('/comunicados/reintentar', { ids: fallidos.map((item) => item._id) });
      const { data: fallidosData } = await api.get('/comunicados/fallidos');
      setFallidos(Array.isArray(fallidosData) ? fallidosData : []);
      alert(`Reintento completado: ${data.enviados} enviados correctamente y ${data.errores} errores.`);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudieron reintentar los comunicados fallidos');
    } finally {
      setReintentando(false);
    }
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Comunicados</h1>
          <p className="page-subtitle">Envía mensajes a clientes concretos o a todos los filtrados.</p>
        </div>
        <button className="btn" onClick={() => navigate('/app/clientes')}>Volver a clientes</button>
      </div>

      {!smtpConfigurado && (
        <div className="alert-warning" style={{ marginBottom: '1rem' }}>
          Falta configurar SMTP. Puedes usar <strong>correoSaliente/correoSalientePassword</strong> en la empresa o las variables globales <strong>EMAIL_USER/EMAIL_PASS</strong>.
        </div>
      )}

      <div className="tabs" style={{ marginBottom: '1rem' }}>
        <button type="button" className={`btn ${vista === 'editor' ? 'btn-primary' : ''}`} onClick={() => setVista('editor')}>Nuevo comunicado</button>
        <button type="button" className={`btn ${vista === 'historial' ? 'btn-primary' : ''}`} onClick={() => setVista('historial')}>Historial</button>
      </div>

      {vista === 'editor' && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <form onSubmit={onEnviar} className="form-grid">
            <label className="field">
              <span>Asunto</span>
              <input value={asunto} onChange={(e) => setAsunto(e.target.value)} placeholder="Ej. Recordatorio de cita y disponibilidad" disabled={!smtpConfigurado} />
            </label>
            <label className="field">
              <span>Tag de campaña</span>
              <input value={campaignTag} onChange={(e) => setCampaignTag(e.target.value)} placeholder="Ej. navidad-2026" disabled={!smtpConfigurado} />
            </label>
            <label className="field">
              <span>Destinatarios</span>
              <select value={scope} onChange={(e) => setScope(e.target.value)} disabled={!smtpConfigurado}>
                <option value="seleccionado">Clientes seleccionados</option>
                <option value="todos">Todos los clientes</option>
              </select>
            </label>
            <label className="field span-2">
              <span>Contenido del comunicado</span>
              <textarea rows={7} value={mensaje} onChange={(e) => setMensaje(e.target.value)} placeholder="Escribe aquí el contenido que quieres enviar a los clientes seleccionados…" disabled={!smtpConfigurado} />
            </label>

            <div className="form-actions span-2">
              <button type="submit" className="btn btn-primary" disabled={enviando || !puedeEnviar}>
                <FiSend /> {enviando ? 'Enviando…' : 'Enviar comunicado'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <div className="toolbar">
          <div className="search-box">
            <FiSearch />
            <input placeholder="Buscar clientes por nombre, email, teléfono o NIF" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
          </div>
          <select className="mini-select" value={filtroCrm} onChange={(e) => setFiltroCrm(e.target.value)}>
            <option value="">Todos los estados CRM</option>
            {ESTADOS_CRM.map((estado) => <option key={estado} value={estado}>{ETIQUETA_CRM[estado]}</option>)}
          </select>
        </div>

        {error && <div className="alert-error">{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <div><strong>{seleccionados.length}</strong> cliente(s) seleccionados</div>
          <button type="button" className="btn" onClick={toggleTodosVisibles}>
            {todasVisiblesSeleccionadas ? 'Quitar visibles' : 'Seleccionar todos los visibles'}
          </button>
        </div>

        {cargando ? (
          <p className="empty">Cargando clientes…</p>
        ) : filtrados.length === 0 ? (
          <p className="empty">No hay clientes con email disponible para enviar comunicados.</p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>
                    <button type="button" className="icon-btn" onClick={toggleTodosVisibles} title="Seleccionar todos los visibles">
                      {todasVisiblesSeleccionadas ? <FiCheckSquare /> : <FiSquare />}
                    </button>
                  </th>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Teléfono</th>
                  <th>Estado CRM</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map((cliente) => (
                  <tr key={cliente._id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={seleccionados.includes(cliente._id)}
                        onChange={() => toggleCliente(cliente._id)}
                      />
                    </td>
                    <td>{cliente.nombre}</td>
                    <td>{cliente.email}</td>
                    <td>{cliente.telefono || '—'}</td>
                    <td>
                      {cliente.estadoCrm ? (
                        <span className={`badge ${cliente.estadoCrm === 'ganado' ? 'badge-ok' : cliente.estadoCrm === 'perdido' ? 'badge-danger' : 'badge'}`}>
                          {ETIQUETA_CRM[cliente.estadoCrm] || cliente.estadoCrm}
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {resultado && (
          <div className="alert-success" style={{ marginTop: '1rem' }}>
            <strong>Comunicado enviado.</strong> {resultado.enviados} enviado(s), {resultado.errores} error(es) de {resultado.total} destinatario(s).
          </div>
        )}

        {vista === 'historial' ? (
          <div style={{ marginTop: '1rem' }}>
            <div className="toolbar" style={{ marginBottom: '1rem' }}>
              <input className="mini-input" placeholder="Buscar cliente o asunto" value={filtroCliente} onChange={(e) => setFiltroCliente(e.target.value)} />
              <select className="mini-select" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                <option value="">Todos los estados</option>
                <option value="enviado">Enviado</option>
                <option value="error">Error</option>
              </select>
              <select className="mini-select" value={filtroScope} onChange={(e) => setFiltroScope(e.target.value)}>
                <option value="">Todos los ámbitos</option>
                <option value="seleccionado">Seleccionado</option>
                <option value="todos">Todos los clientes</option>
              </select>
              <input className="mini-input" placeholder="Tag de campaña" value={filtroCampanya} onChange={(e) => setFiltroCampanya(e.target.value)} />
              <input type="date" className="mini-input" value={filtroDesde} onChange={(e) => setFiltroDesde(e.target.value)} />
              <input type="date" className="mini-input" value={filtroHasta} onChange={(e) => setFiltroHasta(e.target.value)} />
              <button type="button" className="btn" onClick={() => {
                const csvRows = [
                  ['Cliente', 'Asunto', 'Destinatario', 'Estado', 'Ámbito', 'Tag', 'Fecha'],
                  ...historial.map((item) => [
                    item.clienteId?.nombre || '',
                    item.asunto || '',
                    item.para || '',
                    item.estado || '',
                    item.scope || '',
                    item.campaignTag || '',
                    item.createdAt ? new Date(item.createdAt).toISOString() : '',
                  ]),
                ];
                const csv = csvRows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'comunicados-historial.csv';
                a.click();
                URL.revokeObjectURL(url);
              }}>Export CSV</button>
            </div>

            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Asunto</th>
                    <th>Destinatario</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {historial.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="empty">No hay registros con estos filtros.</td>
                    </tr>
                  ) : (
                    historial.map((item) => (
                      <tr key={item._id}>
                        <td>{item.clienteId?.nombre || '—'}</td>
                        <td>{item.asunto || '—'}</td>
                        <td>{item.para || '—'}</td>
                        <td>
                          <span className={`badge ${item.estado === 'error' ? 'badge-danger' : 'badge-ok'}`}>
                            {item.estado === 'error' ? 'Error' : 'Enviado'}
                          </span>
                        </td>
                        <td>
                          <div>{item.scope === 'todos' ? 'Todos' : 'Seleccionado'}</div>
                          <small>{item.campaignTag || 'Sin tag'}</small>
                          <div>{new Date(item.createdAt).toLocaleString('es-ES')}</div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <>
            {fallidos.length > 0 && (
              <div className="card" style={{ marginTop: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0 }}>Comunicados fallidos</h3>
                  <button type="button" className="btn" onClick={onReintentarFallidos} disabled={reintentando}>
                    {reintentando ? 'Reintentando…' : 'Reintentar fallidos'}
                  </button>
                </div>

                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Destinatario</th>
                        <th>Asunto</th>
                        <th>Estado</th>
                        <th>Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fallidos.slice(0, 10).map((item) => (
                        <tr key={item._id}>
                          <td>{item.clienteId?.nombre || item.para || '—'}</td>
                          <td>{item.asunto || '—'}</td>
                          <td><span className="badge badge-danger">Error</span></td>
                          <td>{new Date(item.createdAt).toLocaleString('es-ES')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
